import type { NavigationBody } from '@ayumia/contracts';
import { Empty } from '../states/States';
import { FORMS_WORKSPACE, MODEL_WORKSPACE, VIEWS_WORKSPACE, type Route } from './router';

/**
 * Studio et droits du tenant (ADR-148) : **présentation seulement**. Le menu et les routes `#/model`,
 * `#/studio/views`, `#/studio/forms` se cachent quand le tenant n'a pas le droit du Studio ; le serveur
 * refuse de toute façon (`TENANT_ENTITLEMENT_MISSING`) et élague déjà la navigation. Droits inconnus
 * (`null`, jeton de développement) : rien n'est caché ici, le serveur seul décide.
 */
export type Entitlements = readonly string[] | null;

type NavItem = NavigationBody['items'][number];

/** Le droit qu'ouvre chaque espace Studio de la navigation. */
const STUDIO_WORKSPACES: Readonly<Record<string, string>> = {
  [MODEL_WORKSPACE]: 'studio.model',
  [VIEWS_WORKSPACE]: 'studio.views',
  [FORMS_WORKSPACE]: 'studio.forms',
};

/** Le droit exigé par une route, ou `null` hors Studio. */
export function studioEntitlementOf(route: Route): string | null {
  if (route.kind === 'model') return 'studio.model';
  if (route.kind === 'views') return 'studio.views';
  if (route.kind === 'forms') return 'studio.forms';
  return null;
}

/** Vrai quand la route est un Studio que le tenant n'a pas (droits connus). */
export function studioClosed(route: Route, entitlements: Entitlements): boolean {
  const required = studioEntitlementOf(route);
  return required !== null && entitlements !== null && !entitlements.includes(required);
}

function pruneItems(items: readonly NavItem[], entitlements: readonly string[]): NavItem[] {
  const kept: NavItem[] = [];
  for (const item of items) {
    const required = item.target?.type === 'workspace' ? STUDIO_WORKSPACES[item.target.ref] : undefined;
    if (required !== undefined && !entitlements.includes(required)) continue;
    if (item.children === undefined) {
      kept.push(item);
      continue;
    }
    const children = pruneItems(item.children, entitlements);
    // Un intitulé de section sans cible ne garde sa place que s'il lui reste une entrée.
    if (children.length === 0 && item.target === undefined) continue;
    kept.push(children.length === 0 ? (({ children: _children, ...rest }) => rest)(item) : { ...item, children: children as [NavItem, ...NavItem[]] });
  }
  return kept;
}

const HOME: NavItem = { item_id: 'home', label: { fr: 'Accueil', en: 'Home' }, icon: 'home', target: { type: 'workspace', ref: 'core.workspace.home' } };

/** Navigation sans les entrées des Studios fermés au tenant. */
export function pruneStudioNavigation(navigation: NavigationBody, entitlements: Entitlements): NavigationBody {
  if (entitlements === null) return navigation;
  const [first, ...rest] = pruneItems(navigation.items, entitlements);
  // Le menu garde toujours une entrée : l'accueil, si tout le reste était un Studio fermé.
  const items: NavigationBody['items'] = first === undefined ? [HOME] : [first, ...rest];
  const pruned: NavigationBody = { ...navigation, items };
  if (navigation.footer_items !== undefined) pruned.footer_items = pruneItems(navigation.footer_items, entitlements);
  if (navigation.user_menu_items !== undefined) pruned.user_menu_items = pruneItems(navigation.user_menu_items, entitlements);
  return pruned;
}

/** Arrivée par l'adresse sur un Studio fermé : un message clair, jamais l'écran du Studio. */
export function StudioClosed() {
  return (
    <Empty title="Ce Studio n’est pas ouvert pour votre organisation" label="Votre abonnement ne comprend pas ce Studio. Votre application, ses écrans et ses données restent disponibles ; pour ouvrir le Studio, contactez l’équipe AYUMIA." />
  );
}
